import { FaceCullingMode, } from "../../api";
import { BackSide, Color, DoubleSide, FrontSide, Material as ThreeMaterial, Matrix3, Matrix4, Mesh, Quaternion as ThreeQuaternion, Vector3, } from "three";
import { Cartographic as ThreeCartographic, Cartographic as PCartographic, Ellipsoid, MathUtils, } from "../../private/math";
import { PickingLineBasicMaterial, PickingMeshBasicMaterial, PickingMeshStandardMaterial, PickingPointsMaterial, } from "../../private/materials";
export class BaseObject {
    constructor(object) {
        this.object = object;
    }
    getPositionCartographic(position) {
        if (typeof position !== "undefined") {
            const p = Ellipsoid.WGS_84.cartographicForPosition(this.object.position, new PCartographic());
            position.longitude = p.longitude;
            position.latitude = p.latitude;
            position.height = p.height;
            return position;
        }
        else {
            return Ellipsoid.WGS_84.cartographicForPosition(this.object.position, new PCartographic());
        }
    }
    setPositionCartographic(position) {
        Ellipsoid.WGS_84.cartesianForCartographic(new PCartographic(position.longitude, position.latitude, position.height), this.object.position);
    }
    getPositionECEF(position) {
        if (typeof position === "undefined") {
            return {
                x: this.object.position.x,
                y: this.object.position.y,
                z: this.object.position.z,
            };
        }
        else {
            position.x = this.object.position.x;
            position.y = this.object.position.y;
            position.z = this.object.position.z;
            return position;
        }
    }
    setPositionECEF(position) {
        if (Array.isArray(position)) {
            this.object.position.x = position[0];
            this.object.position.y = position[1];
            this.object.position.z = position[2];
        }
        else {
            this.object.position.x = position.x;
            this.object.position.y = position.y;
            this.object.position.z = position.z;
        }
    }
    getRotation(rotation) {
        if (typeof rotation === "undefined") {
            return {
                x: this.object.quaternion.x,
                y: this.object.quaternion.y,
                z: this.object.quaternion.z,
                w: this.object.quaternion.w,
            };
        }
        else {
            rotation.x = this.object.quaternion.x;
            rotation.y = this.object.quaternion.y;
            rotation.z = this.object.quaternion.z;
            rotation.w = this.object.quaternion.w;
            return rotation;
        }
    }
    setRotation(rotation) {
        this.object.quaternion.x = rotation.x;
        this.object.quaternion.y = rotation.y;
        this.object.quaternion.z = rotation.z;
        this.object.quaternion.w = rotation.w;
    }
    static setObjectRotationCartographic(object, cartographic, heading) {
        // Convert input to our math class
        const cartographicC = new ThreeCartographic(cartographic.longitude, cartographic.latitude, cartographic.height);
        // Get a quaternion that represents the rotation of the heading
        const headingQ = new ThreeQuaternion().setFromAxisAngle(MathUtils.Y_AXIS.clone().negate(), heading !== null && heading !== void 0 ? heading : 0);
        // Extract a rotation basis from the cartographic position as a rotation matrix
        const matrix3 = new Matrix3();
        const up = new Vector3();
        Ellipsoid.WGS_84.rotationBasisForCartographic(cartographicC, matrix3, up);
        // Annoying chain to convert to a quaternion
        const matrix4 = new Matrix4().setFromMatrix3(matrix3);
        const basisQuaternion = new ThreeQuaternion().setFromRotationMatrix(matrix4);
        // Apply the full rotation defined by heading and basis
        object.setRotation(basisQuaternion.multiply(headingQ));
    }
    setRotationCartographic(cartographic, heading) {
        BaseObject.setObjectRotationCartographic(this, cartographic, heading);
    }
    lookAt(at) {
        this.object.lookAt(new Vector3(at.x, at.y, at.z));
    }
    setUpVector(up) {
        this.object.up.x = up.x;
        this.object.up.y = up.y;
        this.object.up.z = up.z;
    }
    getUpVector(target) {
        if (typeof target !== "undefined") {
            target.x = this.object.up.x;
            target.y = this.object.up.y;
            target.z = this.object.up.z;
            return target;
        }
        else {
            return {
                x: this.object.up.x,
                y: this.object.up.y,
                z: this.object.up.z,
            };
        }
    }
    getEstimatedCenter(center) {
        return this.getPositionECEF(center);
    }
    getBoundingVolume() {
        throw new Error("Method not implemented.");
    }
    getObjectType() {
        throw new Error("Method not implemented.");
    }
    setClippingPlanes(planes) {
        if (this.object instanceof Mesh) {
            if (this.object.material instanceof ThreeMaterial) {
                this.object.material.clippingPlanes = planes;
                this.object.material.needsUpdate = true;
            }
            else {
                throw new Error("Unreachable");
            }
        }
    }
    invalidateMaterials() {
        if (this.object instanceof Mesh) {
            if (this.object.material instanceof ThreeMaterial) {
                this.object.material.needsUpdate = true;
            }
            else {
                throw new Error("Unreachable");
            }
        }
    }
    get isTransparent() {
        return false;
    }
    asCameraObject() {
        return null;
    }
    asDrawableObject() {
        return null;
    }
    asMaterialObject() {
        return null;
    }
    asScalableObject() {
        return null;
    }
    asStylableObject() {
        return null;
    }
    asBlendableObject() {
        return null;
    }
    asTileset() {
        return null;
    }
    asIfc() {
        return null;
    }
}
/**
 * Utility function that takes our own Material definition and produces a three.js material that respects the provided
 * material description.
 */
export function constructMeshThreeMaterial(material, clippingPlanes, objectId, userIdChannel, instanceIdAsExtraId) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    switch (material.type) {
        case "basicOpaque": {
            return new PickingMeshBasicMaterial({
                color: new Color(material.color.r, material.color.g, material.color.b),
                vertexColors: (_a = material.vertexColors) !== null && _a !== void 0 ? _a : false,
                wireframe: (_b = material.wireframe) !== null && _b !== void 0 ? _b : false,
                side: ourCullingToThreeCulling(material.faceCulling),
                objectId,
                clippingPlanes,
                userIdChannel,
                instanceIdAsExtraId,
            });
        }
        case "basicAlpha": {
            return new PickingMeshBasicMaterial({
                color: new Color(material.color.r, material.color.g, material.color.b),
                opacity: material.alpha,
                vertexColors: (_c = material.vertexColors) !== null && _c !== void 0 ? _c : false,
                wireframe: (_d = material.wireframe) !== null && _d !== void 0 ? _d : false,
                side: ourCullingToThreeCulling(material.faceCulling),
                objectId,
                clippingPlanes,
                transparent: true,
                forceSinglePass: true,
                depthWrite: false,
                userIdChannel,
                instanceIdAsExtraId,
            });
        }
        case "physicalOpaque": {
            return new PickingMeshStandardMaterial({
                color: new Color(material.baseColor.r, material.baseColor.g, material.baseColor.b),
                metalness: material.metallic,
                roughness: material.roughness,
                vertexColors: (_e = material.vertexColors) !== null && _e !== void 0 ? _e : false,
                wireframe: (_f = material.wireframe) !== null && _f !== void 0 ? _f : false,
                side: ourCullingToThreeCulling(material.faceCulling),
                objectId,
                clippingPlanes,
                userIdChannel,
                instanceIdAsExtraId,
            });
        }
        case "physicalAlpha": {
            return new PickingMeshStandardMaterial({
                color: new Color(material.baseColor.r, material.baseColor.g, material.baseColor.b),
                opacity: material.alpha,
                metalness: material.metallic,
                roughness: material.roughness,
                vertexColors: (_g = material.vertexColors) !== null && _g !== void 0 ? _g : false,
                wireframe: (_h = material.wireframe) !== null && _h !== void 0 ? _h : false,
                side: ourCullingToThreeCulling(material.faceCulling),
                objectId,
                clippingPlanes,
                transparent: true,
                forceSinglePass: true,
                depthWrite: false,
                userIdChannel,
                instanceIdAsExtraId,
            });
        }
        default:
            throw new Error("unreachable");
    }
}
/**
 * Utility function that takes our own Material definition and produces a three.js material that respects the provided
 * material description.
 * @param material
 * @param clippingPlanes
 * @param objectId
 * @param userIdChannel
 */
export function constructPointsThreeMaterial(material, clippingPlanes, objectId, userIdChannel) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    switch (material.type) {
        case "basicOpaque": {
            return new PickingPointsMaterial({
                color: new Color(material.color.r, material.color.g, material.color.b),
                size: (_a = material.pointSize) !== null && _a !== void 0 ? _a : 1,
                vertexColors: (_b = material.vertexColors) !== null && _b !== void 0 ? _b : false,
                side: ourCullingToThreeCulling(material.faceCulling),
                clippingPlanes,
                objectId,
                sizeAttenuation: false,
                userIdChannel,
            });
        }
        case "basicAlpha": {
            return new PickingPointsMaterial({
                color: new Color(material.color.r, material.color.g, material.color.b),
                opacity: material.alpha,
                size: (_c = material.pointSize) !== null && _c !== void 0 ? _c : 1,
                vertexColors: (_d = material.vertexColors) !== null && _d !== void 0 ? _d : false,
                side: ourCullingToThreeCulling(material.faceCulling),
                clippingPlanes,
                objectId,
                transparent: true,
                forceSinglePass: true,
                depthWrite: false,
                sizeAttenuation: false,
                userIdChannel,
            });
        }
        case "physicalOpaque": {
            return new PickingPointsMaterial({
                color: new Color(material.baseColor.r, material.baseColor.g, material.baseColor.b),
                size: (_e = material.pointSize) !== null && _e !== void 0 ? _e : 1,
                vertexColors: (_f = material.vertexColors) !== null && _f !== void 0 ? _f : false,
                side: ourCullingToThreeCulling(material.faceCulling),
                clippingPlanes,
                objectId,
                sizeAttenuation: false,
                userIdChannel,
            });
        }
        case "physicalAlpha": {
            return new PickingPointsMaterial({
                color: new Color(material.baseColor.r, material.baseColor.g, material.baseColor.b),
                opacity: material.alpha,
                size: (_g = material.pointSize) !== null && _g !== void 0 ? _g : 1,
                vertexColors: (_h = material.vertexColors) !== null && _h !== void 0 ? _h : false,
                side: ourCullingToThreeCulling(material.faceCulling),
                clippingPlanes,
                objectId,
                transparent: true,
                forceSinglePass: true,
                depthWrite: false,
                sizeAttenuation: false,
                userIdChannel,
            });
        }
        default:
            throw new Error("unreachable");
    }
}
/**
 * Utility function that takes our own Material definition and produces a three.js material that respects the provided
 * material description.
 * @param material
 * @param clippingPlanes
 * @param objectId
 * @param userIdChannel
 */
export function constructLineMaterial(material, clippingPlanes, objectId, userIdChannel) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    switch (material.type) {
        case "basicOpaque": {
            return new PickingLineBasicMaterial({
                // @ts-ignore
                color: new Color(material.color.r, material.color.g, material.color.b),
                vertexColors: (_a = material.vertexColors) !== null && _a !== void 0 ? _a : false,
                wireframe: (_b = material.wireframe) !== null && _b !== void 0 ? _b : false,
                side: ourCullingToThreeCulling(material.faceCulling),
                clippingPlanes,
                objectId,
                userIdChannel,
            });
        }
        case "basicAlpha": {
            return new PickingLineBasicMaterial({
                // @ts-ignore
                color: new Color(material.color.r, material.color.g, material.color.b),
                opacity: material.alpha,
                vertexColors: (_c = material.vertexColors) !== null && _c !== void 0 ? _c : false,
                wireframe: (_d = material.wireframe) !== null && _d !== void 0 ? _d : false,
                side: ourCullingToThreeCulling(material.faceCulling),
                clippingPlanes,
                objectId,
                transparent: true,
                forceSinglePass: true,
                depthWrite: false,
                userIdChannel,
            });
        }
        case "physicalOpaque": {
            return new PickingLineBasicMaterial({
                // @ts-ignore
                color: new Color(material.baseColor.r, material.baseColor.g, material.baseColor.b),
                vertexColors: (_e = material.vertexColors) !== null && _e !== void 0 ? _e : false,
                wireframe: (_f = material.wireframe) !== null && _f !== void 0 ? _f : false,
                side: ourCullingToThreeCulling(material.faceCulling),
                clippingPlanes,
                objectId,
                userIdChannel,
            });
        }
        case "physicalAlpha": {
            return new PickingLineBasicMaterial({
                // @ts-ignore
                color: new Color(material.baseColor.r, material.baseColor.g, material.baseColor.b),
                opacity: material.alpha,
                vertexColors: (_g = material.vertexColors) !== null && _g !== void 0 ? _g : false,
                wireframe: (_h = material.wireframe) !== null && _h !== void 0 ? _h : false,
                side: ourCullingToThreeCulling(material.faceCulling),
                clippingPlanes,
                objectId,
                transparent: true,
                forceSinglePass: true,
                depthWrite: false,
                userIdChannel,
            });
        }
        default:
            throw new Error("unreachable");
    }
}
export function ourCullingToThreeCulling(faceCulling) {
    if (typeof faceCulling !== "undefined") {
        switch (faceCulling) {
            case FaceCullingMode.BackFace:
                return BackSide;
            case FaceCullingMode.FrontFace:
                return FrontSide;
            case FaceCullingMode.None:
                return DoubleSide;
        }
    }
    else {
        return undefined;
    }
}
